import { format, formatDistanceToNow, parseISO } from 'date-fns';
import type { ChipTone } from '@/components/editorial';
import type { ResourceCategory, ResourceCountry, ResourceState, ResourceTreatmentPhase } from '@/types/api';

export const CATEGORY_LABELS: Record<ResourceCategory, string> = {
  financial: 'Financial',
  housing: 'Housing',
  medical: 'Medical',
  counseling: 'Counseling',
  legal: 'Legal',
  community: 'Community',
  other: 'Other',
};

export const CATEGORY_ORDER: ResourceCategory[] = [
  'financial', 'housing', 'medical', 'counseling', 'legal', 'community', 'other',
];

export const COUNTRY_LABELS: Record<ResourceCountry, string> = { US: 'United States', CA: 'Canada' };

export const PHASE_LABELS: Record<ResourceTreatmentPhase, string> = {
  diagnosis: 'Diagnosis',
  active_treatment: 'Active treatment',
  maintenance: 'Maintenance',
  survivorship: 'Survivorship',
  off_treatment: 'Off treatment',
  bereaved: 'Bereaved',
  other: 'Other',
};

export const PHASE_ORDER: ResourceTreatmentPhase[] = [
  'diagnosis', 'active_treatment', 'maintenance', 'survivorship', 'off_treatment', 'bereaved', 'other',
];

/**
 * Geography axis. Families never pick their own state — it is derived from the
 * hospital their application named — so this table exists for the staff form
 * and the staff preview only.
 */
export const STATE_LABELS: Record<ResourceState, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'Washington, D.C.',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan',
  MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
  NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota',
  OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
  RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee',
  TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
  WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  AS: 'American Samoa', GU: 'Guam', MP: 'Northern Mariana Islands',
  PR: 'Puerto Rico', VI: 'U.S. Virgin Islands',
  AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba', NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador', NS: 'Nova Scotia', NT: 'Northwest Territories',
  NU: 'Nunavut', ON: 'Ontario', PE: 'Prince Edward Island', QC: 'Quebec',
  SK: 'Saskatchewan', YT: 'Yukon',
};

/** Alphabetical by label, US first then Canada — the order staff scan in. */
export const STATE_ORDER: ResourceState[] = (Object.keys(STATE_LABELS) as ResourceState[])
  .sort((a, b) => STATE_LABELS[a].localeCompare(STATE_LABELS[b]));

/**
 * Human summary of an audience for the staff row, e.g. "United States ·
 * Nevada · Bereaved" or "All families". Countries print as words (§1.2 —
 * never the stored code; the states already did).
 */
export function audienceSummary(
  audience: {
    countries: ResourceCountry[];
    treatment_phases: ResourceTreatmentPhase[];
    states?: ResourceState[];
  },
): string {
  const parts: string[] = [];
  if (audience.countries.length) parts.push(audience.countries.map((c) => COUNTRY_LABELS[c] ?? c).join(', '));
  if (audience.states?.length) parts.push(audience.states.map((s) => STATE_LABELS[s] ?? s).join(', '));
  if (audience.treatment_phases.length) parts.push(audience.treatment_phases.map((p) => PHASE_LABELS[p]).join(', '));
  return parts.length ? parts.join(' · ') : 'All families';
}

/** The host of a resource's link ("211.org"), for the row's small type; null when there is none. */
export function resourceLinkHost(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace(/^www\./, '') || null;
  } catch {
    return null;
  }
}

/** Published (ok) · Draft (neutral) — a tone NAME for `StatusChip`, never a class string. */
export function resourceStatusChip(isPublished: boolean): { label: string; tone: ChipTone } {
  return isPublished ? { label: 'Published', tone: 'ok' } : { label: 'Draft', tone: 'neutral' };
}

/**
 * The Updated cell (desk pages wave, S10): a relative phrase on the line
 * ("3 days ago") with the absolute date on hover. `Resource.update` stamps
 * `updated_at` on every edit, the publish toggle included, so the column is
 * honest. An unparseable stamp prints "—" with the reason on hover.
 */
export function resourceUpdatedCell(iso: string | null | undefined): { text: string; title: string } {
  if (!iso) return { text: '—', title: 'No update recorded' };
  const date = parseISO(iso);
  if (Number.isNaN(date.getTime())) return { text: '—', title: 'No update recorded' };
  return {
    text: formatDistanceToNow(date, { addSuffix: true }),
    title: `Updated ${format(date, 'MMM d, yyyy')}`,
  };
}

/**
 * `(?) resource-stale-after-180d` (w1023c L3): an entry not checked for this
 * many days is due for a check. Mirrors `RESOURCE_STALE_AFTER_DAYS` in
 * backend/src/models/Resource.js.
 */
export const RESOURCE_STALE_AFTER_DAYS = 180;
const DAY_MS = 24 * 60 * 60 * 1000;

function parseInstant(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * True when the entry is due for a check: never checked (every row created
 * before `last_checked_at` existed, no migration), unparseable, or checked
 * more than 180 days before `now`. Elapsed time, so the reader's zone does
 * not move the line.
 */
export function isResourceCheckDue(lastCheckedAt: string | null | undefined, now: Date = new Date()): boolean {
  const d = parseInstant(lastCheckedAt);
  if (!d) return true;
  return now.getTime() - d.getTime() > RESOURCE_STALE_AFTER_DAYS * DAY_MS;
}

/**
 * The month an entry was checked, "Oct 2026", in the READER's zone and
 * language (an instant near a month edge lands on the reader's side of it).
 * Null when never checked. `timeZone` and `locale` exist for tests and the
 * family's language; omitted, the browser's own are used.
 */
export function resourceCheckedMonth(
  lastCheckedAt: string | null | undefined,
  opts: { timeZone?: string; locale?: string } = {},
): string | null {
  const d = parseInstant(lastCheckedAt);
  if (!d) return null;
  return new Intl.DateTimeFormat(opts.locale ?? 'en-US', {
    month: 'short', year: 'numeric', timeZone: opts.timeZone,
  }).format(d);
}

/**
 * The staff row's check cell: "Checked Oct 2026" or "Never checked", with
 * the full date on hover, and whether the "Check due" chip shows.
 */
export function resourceCheckedCell(
  lastCheckedAt: string | null | undefined,
  now: Date = new Date(),
  opts: { timeZone?: string } = {},
): { text: string; title: string; due: boolean } {
  const due = isResourceCheckDue(lastCheckedAt, now);
  const month = resourceCheckedMonth(lastCheckedAt, opts);
  if (!month) return { text: 'Never checked', title: 'No one has marked this entry checked yet', due };
  const day = new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: opts.timeZone,
  }).format(new Date(lastCheckedAt as string));
  return { text: `Checked ${month}`, title: `Last checked ${day}`, due };
}

/** A report's day, "Oct 20, 2026", in the reader's zone; "—" when unparseable. */
export function resourceReportDate(iso: string | null | undefined, opts: { timeZone?: string } = {}): string {
  const d = parseInstant(iso);
  if (!d) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: opts.timeZone,
  }).format(d);
}

/** "Reported 3×" — null when no family has reported the entry since its last check. */
export function resourceReportedLabel(reports: ReadonlyArray<unknown> | null | undefined): string | null {
  const n = reports?.length ?? 0;
  return n > 0 ? `Reported ${n}×` : null;
}

/**
 * A TRF group (`(?) trf-group-flag`): a Community entry TRF runs. Shows on
 * Community; the server refuses the flag on any other category.
 */
export function isTrfGroup(r: { category: ResourceCategory; run_by_trf?: boolean }): boolean {
  return r.category === 'community' && r.run_by_trf === true;
}

/**
 * A WhatsApp invite or chat link (`chat.whatsapp.com/...` or `wa.me/...`).
 * The TRF-groups list labels its button "Join on WhatsApp" for these and
 * "Join group" for anything else.
 */
export function isWhatsAppLink(url: string | null | undefined): boolean {
  if (!url) return false;
  try {
    const host = new URL(url).hostname.toLowerCase().replace(/^www\./, '');
    return host === 'chat.whatsapp.com' || host === 'wa.me';
  } catch {
    return false;
  }
}
